import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Ban, Plus, Trash2 } from "lucide-react";
import { PageHeader } from "../../components/layout/PageHeader";
import { Modal } from "../../components/ui/Modal";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { useSession } from "../../hooks/useSession";
import {
  adminNumbersApi,
  type AdminNumberDetail,
  type Lookup,
  type NumberAssignment,
  type NumberVariable,
  type SaveNumber,
} from "../../api/adminNumbersApi";
import { customersAdminApi, type AdminCustomerListItem } from "../../api/customersAdminApi";
import { FAR_FUTURE, fromDateInput, toDateInput, today } from "../../lib/dates";


// Matches the app's shared `.field` input styling (see app.css).
const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "14px 16px",
  border: "2px solid var(--portal-border)",
  borderRadius: 8,
  fontSize: "1rem",
  fontFamily: "inherit",
  background: "white",
};

const fieldGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 14,
};
// Full-width list rows (not cards) for assignments / variables.
const listStack: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
};
const listRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 2px",
  borderTop: "1px solid var(--portal-border)",
};


type LoadState =
  | { status: "loading" }
  | { status: "ready"; detail: AdminNumberDetail }
  | { status: "new" }
  | { status: "error"; message: string };

/** Admin/manager number detail: edit core fields, manage assignments and variables. */
export function AdminNumberEditPage() {
  const { dnisId: param } = useParams<{ dnisId: string }>();
  const isNew = param === "new";
  const dnisId = Number(param);
  const navigate = useNavigate();
  const { session } = useSession();
  const isAdmin = session?.kind === "Admin";

  const [state, setState] = useState<LoadState>(isNew ? { status: "new" } : { status: "loading" });
  const [types, setTypes] = useState<Lookup[]>([]);
  const [scripts, setScripts] = useState<Lookup[]>([]);
  const [providers, setProviders] = useState<Lookup[]>([]);

  useEffect(() => {
    adminNumbersApi.numberTypes().then(setTypes).catch(() => undefined);
    adminNumbersApi.scripts().then(setScripts).catch(() => undefined);
    adminNumbersApi.providers().then(setProviders).catch(() => undefined);
  }, []);

  const load = useCallback(async () => {
    if (isNew) return;
    if (!Number.isFinite(dnisId) || dnisId <= 0) {
      setState({ status: "error", message: "Invalid number id." });
      return;
    }
    try {
      setState({ status: "ready", detail: await adminNumbersApi.get(dnisId) });
    } catch (err) {
      setState({ status: "error", message: err instanceof Error ? err.message : "Failed to load number" });
    }
  }, [dnisId, isNew]);

  useEffect(() => {
    void load();
  }, [load]);

  const title =
    state.status === "ready"
      ? state.detail.dnisPresentation || state.detail.dnis || "Number"
      : isNew
        ? "New number"
        : "Number";

  return (
    <>
      <PageHeader title={title} actions={<BackLink />} />
      <div className="dashboard">
        {state.status === "loading" && (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>Loading number…</div>
        )}
        {state.status === "error" && (
          <div className="tile" style={{ borderColor: "#e74c3c", color: "#c0392b", background: "#fdecea" }}>
            <strong>Could not load this number.</strong>
            <div style={{ marginTop: 6, fontSize: "0.9rem" }}>{state.message}</div>
            <button type="button" className="btn btn-secondary" style={{ marginTop: 12 }} onClick={() => navigate("/admin/numbers")}>
              Back to numbers
            </button>
          </div>
        )}
        {(state.status === "ready" || state.status === "new") && (
          <NumberForm
            detail={state.status === "ready" ? state.detail : null}
            isAdmin={isAdmin}
            types={types}
            scripts={scripts}
            providers={providers}
            onCreated={(id) => navigate(`/admin/numbers/${id}`)}
            onSaved={(d) => setState({ status: "ready", detail: d })}
          />
        )}
        {state.status === "ready" && (
          <>
            <AssignmentsSection dnisId={dnisId} onEnded={load} />
            <VariablesSection dnisId={dnisId} />
          </>
        )}
      </div>
    </>
  );
}

function NumberForm({
  detail,
  isAdmin,
  types,
  scripts,
  providers,
  onCreated,
  onSaved,
}: {
  detail: AdminNumberDetail | null;
  isAdmin: boolean;
  types: Lookup[];
  scripts: Lookup[];
  providers: Lookup[];
  onCreated: (dnisId: number) => void;
  onSaved: (d: AdminNumberDetail) => void;
}) {
  const isNew = detail == null;
  const [dnis, setDnis] = useState(detail?.dnis ?? "");
  const [presentation, setPresentation] = useState(detail?.dnisPresentation ?? "");
  const [extra, setExtra] = useState(detail?.dnisExtra ?? "");
  const [providerId, setProviderId] = useState<number>(detail?.providerId ?? 0);
  const [numberTypeId, setNumberTypeId] = useState<number>(detail?.numberTypeId ?? 0);
  const [scriptId, setScriptId] = useState<number>(detail?.scriptId ?? 0);
  const [countryCode, setCountryCode] = useState(detail?.countryCode ?? "");
  const [areaCode, setAreaCode] = useState(detail?.areaCode ?? "");
  const [description, setDescription] = useState(detail?.description ?? "");
  const [perCall, setPerCall] = useState(detail?.userTariffPerCall != null ? String(detail.userTariffPerCall) : "");
  const [perMinute, setPerMinute] = useState(detail?.userTariffPerMinute != null ? String(detail.userTariffPerMinute) : "");
  const [validFrom, setValidFrom] = useState(toDateInput(detail?.validFromUtc) || today());
  const [validTill, setValidTill] = useState(toDateInput(detail?.validTillUtc) || FAR_FUTURE);
  const [active, setActive] = useState(detail?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // A manager cannot re-provision carrier fields on a brand-new number
  // (creation is admin-only); on an existing number they can edit.
  const build = (): SaveNumber => ({
    providerId,
    dnis: dnis.trim(),
    dnisPresentation: presentation.trim(),
    dnisExtra: extra.trim(),
    numberTypeId,
    scriptId,
    description: description.trim(),
    userTariffPerCall: perCall === "" ? null : Number(perCall),
    userTariffPerMinute: perMinute === "" ? null : Number(perMinute),
    validFromUtc: fromDateInput(validFrom, today()),
    validTillUtc: fromDateInput(validTill, FAR_FUTURE),
    active,
    countryCode: countryCode.trim(),
    areaCode: areaCode.trim(),
    // Pass-through: preserved from the loaded detail (no dropdowns).
    preferredSwitchId: detail?.preferredSwitchId ?? null,
    ringTypeId: detail?.ringTypeId ?? null,
  });

  const save = async () => {
    if (!dnis.trim() || !presentation.trim()) {
      setError("DNIS and presentation are required.");
      return;
    }
    setError(null);
    setBusy(true);
    setSaved(false);
    try {
      if (isNew) {
        const { dnisId } = await adminNumbersApi.create(build());
        onCreated(dnisId);
      } else {
        onSaved(await adminNumbersApi.update(detail!.dnisId, build()));
        setSaved(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save number");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="tile" style={{ gap: 14 }}>
      <header className="tile-head">
        <h3 style={{ margin: 0 }}>Details</h3>
      </header>
      {error && <ErrorLine message={error} />}
      {detail && (
        <div style={{ color: "var(--portal-muted)", fontSize: "0.85rem" }}>
          {detail.managerName || "—"}
          {detail.customerName ? ` · ${detail.customerName}` : ""}
        </div>
      )}

      <div style={fieldGrid}>
        <Field label="DNIS"><input className="portal-input-inline" style={inputStyle} value={dnis} onChange={(e) => setDnis(e.target.value)} /></Field>
        <Field label="Presentation"><input className="portal-input-inline" style={inputStyle} value={presentation} onChange={(e) => setPresentation(e.target.value)} /></Field>
        <Field label="DNIS extra"><input className="portal-input-inline" style={inputStyle} value={extra} onChange={(e) => setExtra(e.target.value)} /></Field>
        <Field label="Provider">
          <select className="portal-input-inline" style={inputStyle} value={providerId} onChange={(e) => setProviderId(Number(e.target.value))}>
            <option value={0}>Select provider…</option>
            {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Number type">
          <select className="portal-input-inline" style={inputStyle} value={numberTypeId} onChange={(e) => setNumberTypeId(Number(e.target.value))}>
            <option value={0}>Select type…</option>
            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        <Field label="Script">
          <select className="portal-input-inline" style={inputStyle} value={scriptId} onChange={(e) => setScriptId(Number(e.target.value))}>
            <option value={0}>Select script…</option>
            {scripts.map((sc) => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
          </select>
        </Field>
        <Field label="Country code"><input className="portal-input-inline" style={inputStyle} value={countryCode} onChange={(e) => setCountryCode(e.target.value)} /></Field>
        <Field label="Area code"><input className="portal-input-inline" style={inputStyle} value={areaCode} onChange={(e) => setAreaCode(e.target.value)} /></Field>
        <Field label="Tariff per call"><input type="number" className="portal-input-inline" style={inputStyle} value={perCall} onChange={(e) => setPerCall(e.target.value)} /></Field>
        <Field label="Tariff per minute"><input type="number" className="portal-input-inline" style={inputStyle} value={perMinute} onChange={(e) => setPerMinute(e.target.value)} /></Field>
        <Field label="Valid from"><input type="date" className="portal-input-inline" style={inputStyle} value={validFrom} onChange={(e) => setValidFrom(e.target.value)} /></Field>
        <Field label="Valid till"><input type="date" className="portal-input-inline" style={inputStyle} value={validTill} onChange={(e) => setValidTill(e.target.value)} /></Field>
      </div>

      <Field label="Description"><input className="portal-input-inline" style={inputStyle} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>

      <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        <span>Active</span>
      </label>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12 }}>
        {saved && <span style={{ color: "var(--portal-status-success-darker)", fontSize: "0.85rem" }}>Saved</span>}
        {isNew && !isAdmin ? (
          <span style={{ color: "var(--portal-muted)", fontSize: "0.85rem" }}>Only admins can create numbers.</span>
        ) : (
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy}>
            {busy ? "Saving…" : isNew ? "Create number" : "Save changes"}
          </button>
        )}
      </div>
    </section>
  );
}

function AssignmentsSection({ dnisId, onEnded }: { dnisId: number; onEnded: () => void }) {
  const [rows, setRows] = useState<NumberAssignment[] | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [ending, setEnding] = useState(false);

  useEffect(() => {
    let active = true;
    adminNumbersApi.assignments(dnisId).then((r) => active && setRows(r)).catch(() => active && setRows([]));
    return () => {
      active = false;
    };
  }, [dnisId]);

  return (
    <section className="tile" style={{ gap: 12 }}>
      <header className="tile-head" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h3 style={{ margin: 0 }}>Assignments</h3>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={() => setEnding(true)} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8rem" }}>
            <Ban size={14} /> End DNIS
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setAssigning(true)} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}>
            <Plus size={14} /> Assign
          </button>
        </div>
      </header>
      {rows == null ? (
        <div style={{ color: "var(--portal-muted)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: "var(--portal-muted)" }}>No assignments.</div>
      ) : (
        <div style={listStack}>
          {rows.map((a, i) => (
            <div key={`${a.customerId}-${i}`} style={listRow}>
              <div style={{ flex: 1, minWidth: 0, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {a.customerName || `Customer #${a.customerId}`}
              </div>
              <div style={{ color: "var(--portal-muted)", fontSize: "0.8rem", flexShrink: 0 }}>
                {fmt(a.validFromUtc)} → {fmt(a.validTillUtc)}
              </div>
            </div>
          ))}
        </div>
      )}

      {assigning && (
        <AssignModal
          dnisId={dnisId}
          onClose={() => setAssigning(false)}
          onSaved={(next) => {
            setRows(next);
            setAssigning(false);
          }}
        />
      )}
      {ending && (
        <EndDnisModal
          dnisId={dnisId}
          onClose={() => setEnding(false)}
          onEnded={() => {
            setEnding(false);
            onEnded();
          }}
        />
      )}
    </section>
  );
}

function AssignModal({ dnisId, onClose, onSaved }: { dnisId: number; onClose: () => void; onSaved: (rows: NumberAssignment[]) => void }) {
  const [customers, setCustomers] = useState<AdminCustomerListItem[]>([]);
  const [customerId, setCustomerId] = useState<number | "">("");
  const [validFrom, setValidFrom] = useState(today());
  const [validTill, setValidTill] = useState(FAR_FUTURE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    customersAdminApi.list().then((r) => setCustomers(r.items ?? [])).catch(() => undefined);
  }, []);

  const save = async () => {
    if (customerId === "") {
      setError("Select a customer.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      onSaved(
        await adminNumbersApi.assign(dnisId, {
          customerId: Number(customerId),
          validFromUtc: fromDateInput(validFrom, today()),
          validTillUtc: fromDateInput(validTill, FAR_FUTURE),
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to assign");
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => !busy && onClose()}
      title="Assign number"
      busy={busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy}>{busy ? "Assigning…" : "Assign"}</button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 10 }}>
        {error && <ErrorLine message={error} />}
        <Field label="Customer">
          <select className="portal-input-inline" style={inputStyle} value={customerId} onChange={(e) => setCustomerId(e.target.value === "" ? "" : Number(e.target.value))}>
            <option value="">Select customer…</option>
            {customers.map((c) => <option key={c.customerId} value={c.customerId}>{c.customerName}</option>)}
          </select>
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Valid from"><input type="date" className="portal-input-inline" style={inputStyle} value={validFrom} onChange={(e) => setValidFrom(e.target.value)} /></Field>
          <Field label="Valid till"><input type="date" className="portal-input-inline" style={inputStyle} value={validTill} onChange={(e) => setValidTill(e.target.value)} /></Field>
        </div>
      </div>
    </Modal>
  );
}

function EndDnisModal({ dnisId, onClose, onEnded }: { dnisId: number; onClose: () => void; onEnded: () => void }) {
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [date, setDate] = useState(tomorrow);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const end = async () => {
    setBusy(true);
    setError(null);
    try {
      await adminNumbersApi.end(dnisId, fromDateInput(date, tomorrow));
      onEnded();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to end DNIS");
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => !busy && onClose()}
      title="End DNIS"
      busy={busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-danger" onClick={() => void end()} disabled={busy}>{busy ? "Ending…" : "End DNIS"}</button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 10 }}>
        {error && <ErrorLine message={error} />}
        <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--portal-muted)" }}>
          The number stops being active from this date.
        </p>
        <Field label="End date"><input type="date" className="portal-input-inline" style={inputStyle} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

function VariablesSection({ dnisId }: { dnisId: number }) {
  const [rows, setRows] = useState<NumberVariable[] | null>(null);
  const [editing, setEditing] = useState<NumberVariable | "new" | null>(null);
  const [removePending, setRemovePending] = useState<NumberVariable | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    adminNumbersApi.variables(dnisId).then((r) => active && setRows(r)).catch(() => active && setRows([]));
    return () => {
      active = false;
    };
  }, [dnisId]);

  const remove = async (variableId: number) => {
    setBusy(true);
    try {
      setRows(await adminNumbersApi.deleteVariable(dnisId, variableId));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="tile" style={{ gap: 12 }}>
      <header className="tile-head" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h3 style={{ margin: 0 }}>Variables</h3>
        <button type="button" className="btn btn-primary" onClick={() => setEditing("new")} style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}>
          <Plus size={14} /> Add variable
        </button>
      </header>
      {rows == null ? (
        <div style={{ color: "var(--portal-muted)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: "var(--portal-muted)" }}>No variables.</div>
      ) : (
        <div style={listStack}>
          {rows.map((v) => (
            <div key={v.variableId} style={listRow}>
              <code style={{ flex: 1, minWidth: 0, fontSize: "0.85rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {v.variableName} = {v.variableValue}
              </code>
              <button type="button" className="btn btn-secondary" onClick={() => setEditing(v)} style={{ fontSize: "0.8rem", flexShrink: 0 }}>Edit</button>
              <button type="button" className="btn btn-secondary" onClick={() => setRemovePending(v)} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8rem", flexShrink: 0 }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <VariableModal
          dnisId={dnisId}
          value={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(next) => {
            setRows(next);
            setEditing(null);
          }}
        />
      )}

      <ConfirmModal
        open={removePending != null}
        onClose={() => !busy && setRemovePending(null)}
        title="Delete variable?"
        confirmLabel="Delete"
        tone="danger"
        busy={busy}
        onConfirm={() => {
          const v = removePending;
          setRemovePending(null);
          if (v) void remove(v.variableId);
        }}
      >
        Delete variable <strong>{removePending?.variableName}</strong>?
      </ConfirmModal>
    </section>
  );
}

function VariableModal({ dnisId, value, onClose, onSaved }: { dnisId: number; value: NumberVariable | null; onClose: () => void; onSaved: (rows: NumberVariable[]) => void }) {
  const [name, setName] = useState(value?.variableName ?? "");
  const [val, setVal] = useState(value?.variableValue ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      onSaved(
        await adminNumbersApi.upsertVariable(dnisId, {
          variableId: value?.variableId ?? 0,
          variableName: name.trim(),
          variableValue: val,
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save variable");
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => !busy && onClose()}
      title={value ? "Edit variable" : "Add variable"}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 10 }}>
        {error && <ErrorLine message={error} />}
        <Field label="Name"><input className="portal-input-inline" style={inputStyle} value={name} maxLength={50} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Value"><input className="portal-input-inline" style={inputStyle} value={val} maxLength={150} onChange={(e) => setVal(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

function BackLink() {
  return (
    <Link
      to="/admin/numbers"
      aria-label="Back to numbers"
      className="btn btn-secondary"
      style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}
    >
      <ArrowLeft size={14} /> Back to numbers
    </Link>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ fontSize: "0.9rem", fontWeight: 700 }}>{label}</span>
      {children}
    </label>
  );
}

function ErrorLine({ message }: { message: string }) {
  return (
    <div style={{ color: "#c0392b", background: "#fdecea", border: "1px solid #e74c3c", borderRadius: 8, padding: 10, fontSize: "0.9rem" }}>
      {message}
    </div>
  );
}

function fmt(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  if (d.getUTCFullYear() >= 2100) return "∞";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
}
